/**
 * Centralized select field options shared across all client apps.
 *
 * The backend sends placeholder strings like ["STATES"] or ["COUNTRIES"]
 * instead of full arrays to keep API responses small. Each app resolves
 * these via OPTIONS_MAP before rendering.
 */

export const US_STATES = [
  "AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DC", "DE", "FL",
  "GA", "HI", "IA", "ID", "IL", "IN", "KS", "KY", "LA", "MA",
  "MD", "ME", "MI", "MN", "MO", "MS", "MT", "NC", "ND", "NE",
  "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WA", "WI", "WV",
  "WY", "PR", "VI", "GU", "AS", "MP",
]

export const COUNTRIES = [
  "United States", "Afghanistan", "Albania", "Algeria", "Andorra", "Angola",
  "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas",
  "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize",
  "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
  "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia",
  "Cameroon", "Canada", "Chad", "Chile", "China", "Colombia", "Congo",
  "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark",
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Estonia",
  "Ethiopia", "Finland", "France", "Germany", "Ghana", "Greece",
  "Guatemala", "Haiti", "Honduras", "Hungary", "Iceland", "India",
  "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica",
  "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Latvia", "Lebanon",
  "Libya", "Lithuania", "Luxembourg", "Malaysia", "Mexico", "Morocco",
  "Netherlands", "New Zealand", "Nicaragua", "Nigeria", "Norway",
  "Pakistan", "Panama", "Paraguay", "Peru", "Philippines", "Poland",
  "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia", "Serbia",
  "Singapore", "Slovakia", "Slovenia", "South Africa", "South Korea",
  "Spain", "Sri Lanka", "Sweden", "Switzerland", "Taiwan", "Thailand",
  "Trinidad and Tobago", "Tunisia", "Turkey", "Ukraine",
  "United Arab Emirates", "United Kingdom", "Uruguay", "Venezuela",
  "Vietnam", "Zimbabwe",
]

export const EXPENSE_CATEGORIES = [
  "Advertising",
  "Car and Truck Expenses",
  "Commissions and Fees",
  "Contract Labor",
  "Depletion",
  "Depreciation",
  "Employee Benefits",
  "Insurance",
  "Interest (Mortgage)",
  "Interest (Other)",
  "Legal and Professional Services",
  "Office Expense",
  "Pension and Profit-Sharing",
  "Rent (Vehicles/Equipment)",
  "Rent (Other)",
  "Repairs and Maintenance",
  "Supplies",
  "Taxes and Licenses",
  "Travel",
  "Meals",
  "Utilities",
  "Wages",
  "Other Expenses",
]

export const FILING_STATUSES = [
  "Single",
  "Married Filing Jointly",
  "Married Filing Separately",
  "Head of Household",
  "Qualifying Widow(er)",
]

export const YES_NO = ["Yes", "No"]

export const USER_TYPES = ["spouse", "taxClient"]

export const W2_BOX12_CODES = [
  "A", "AA", "B", "BB", "C", "D", "DD", "E", "EE", "F", "FF",
  "G", "GG", "H", "HH", "II", "J", "K", "L", "M", "N",
  "P", "Q", "R", "S", "T", "V", "W", "Y", "Z",
]

/**
 * Maps backend placeholder strings to actual option arrays.
 * When the backend sends options: ["STATES"], the client resolves
 * via OPTIONS_MAP["STATES"] to get the full array.
 */
export const OPTIONS_MAP: Record<string, string[]> = {
  STATES: US_STATES,
  COUNTRIES: COUNTRIES,
  COUNTRY: COUNTRIES,
  EXPENSE_CATEGORIES,
  FILING_STATUSES,
  YES_NO,
  USER_TYPES,
  W2_BOX12_CODES,
}

/**
 * Resolve placeholder option arrays to actual values.
 * If options is ["STATES"], returns the full US_STATES array.
 * If options is already a real list, returns as-is.
 */
export function resolveOptions(options?: string[]): string[] | undefined {
  if (!options || options.length === 0) return options
  if (options.length === 1 && options[0] in OPTIONS_MAP) {
    return [...OPTIONS_MAP[options[0]]]
  }
  return options
}
